import { useCallback } from 'react';
import { App, Col, Divider, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { FeatureCard, DeviceWrapper } from '@/components';
import { useUniversityContext, useUserContext } from '@/context';
import { SwiperConfigable, SwiperItemAttrs } from '@/components/SwiperConfigable';
import { featureList, FeatureItem } from '../featureListData';

import './ConfigurableMode.css';

export interface ConfigurableModeProps {
  variant: ComponentVariant;
  loading: boolean;
  error: string;
  items: SwiperItemAttrs[];
  onActionClick?: (item: SwiperItemAttrs) => any;
  onCallApiClick?: () => any;

}

export function ConfigurableMode({
  variant,
  loading,
  error,
  items,
  onActionClick = undefined,
  onCallApiClick = undefined
}: ConfigurableModeProps) {

  const navigate = useNavigate();
  const [t] = useTranslation(['common']);
  const { university } = useUniversityContext();
  const { profile, roles } = useUserContext();
  const { message } = App.useApp();

  const features = featureList(university, profile, roles, message, t)[variant];

  const onFeatureClick = useCallback((item: FeatureItem) => {
    if (item.enable) {
      if (item.onClick) {
        item.onClick(university.universityURL);
      }
      else if (item.target) {
        navigate({
          pathname: item.target,
          search: `?${createSearchParams(item.query)}`
        });
      }
    }
  }, [university]);

  return (
    <>
      <DeviceWrapper show={['desktop']}>
        <SwiperConfigable
          error={error}
          items={items}
          variant={variant}
          loading={loading}
          onActionClick={onActionClick}
        />
      </DeviceWrapper>
      <DeviceWrapper show={['mobile']}>
        <SwiperConfigable
          smallButton
          scale={true}
          error={error}
          card={false}
          compact={false}
          items={items}
          loading={loading}
          centeredSlides={true}
          spaceBetween={0}
          layout="column"
          pagination={{ enabled: false }}
          slidesPerView="auto"
          direction="horizontal"
          variant={variant}
          onActionClick={onActionClick}
          onCallApiClick={onCallApiClick}
          style={{
            // height: 'min(70%)',
            height: 'auto',
            marginBlock: '0',
            marginBottom: 'max(2.25vh, 0.25rem)',
            marginTop: 0
          }}
        />
        <Divider className={`!my-2 border-${variant}-darker`} />
        <Row className="w-full text-white" gutter={[12, 12]} style={{ marginTop: 'max(2.25vh, 0.25rem)' }}>
          {features.map((item, i) => {
            const disabled = item.samadKey && !university?.samad[item?.samadKey];
            return (
              <Col key={i} span={8} md={6}>
                <FeatureCard
                  variant={variant}
                  icon={item.icon}
                  title={item.mobileText ?? item.text}
                  disabled={disabled}
                  onClick={() => onFeatureClick(item)}
                />
              </Col>
            );
          })}
        </Row>
      </DeviceWrapper>
    </>
  );
};

export default ConfigurableMode;
